import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  Users,
  Calendar,
  Activity,
  Brain,
  Smile,
  ArrowRight
} from 'lucide-react';
import homepage_photo from '../assets/homepage_images/homepage_photo.jpg';
import about1 from '../assets/aboutpage_images/about1.jpg';
import about2 from '../assets/aboutpage_images/about2.jpg';
import about3 from '../assets/aboutpage_images/about3.jpg';
import about4 from '../assets/aboutpage_images/about4.jpg';
import ImageCarousel from '../components/ImageCarousel';

const Home = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  const features = [
    {
      icon: Smile,
      title: 'Child-Friendly Environment',
      description: 'Safe, welcoming, and nurturing spaces designed specifically for children to feel comfortable and secure.'
    },
    {
      icon: Activity,
      title: 'Goal-Based Therapy',
      description: 'Structured programs tailored to individual developmental needs with clear milestones and progress tracking.'
    },
    {
      icon: Users,
      title: 'Experienced Professionals',
      description: 'Our multidisciplinary team consists of highly trained therapists dedicated to your child\'s success.'
    },
    {
      icon: Brain,
      title: 'Sensory Integration',
      description: 'State-of-the-art sensory gyms and equipment to support comprehensive developmental therapy.'
    }
  ];

  const programs = [
    'Autism Spectrum Disorder',
    'Cerebral Palsy',
    'Learning Disabilities',
    'Developmental Delays',
    'Behavioral Challenges',
    'ADHD / ADD',
    'Down Syndrome',
    'Sensory Processing'
  ];

  return (
    <div className="font-sans text-[#424242]">
      {/* Hero Section */}
      <div className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={homepage_photo}
            alt="Kids Work Therapy Session"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/90 to-violet-900/80 backdrop-blur-[2px]"></div>
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto px-5 text-center text-white pt-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-sm font-medium mb-6 animate-fade-in-up">
            <Heart className="w-4 h-4 text-pink-300" fill="currentColor" />
            <span>Dedicated to Your Child's Growth</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-wide">
            Empowering Children <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-white">
              To Reach Their Potential
            </span>
          </h1>

          <p className="text-lg md:text-xl text-indigo-100 max-w-[800px] mx-auto mb-10 leading-relaxed font-light">
            Comprehensive rehabilitation services with personalized care for children with neurological issues and developmental delays. We believe every child deserves the chance to shine.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/branches"
              className="group bg-white text-indigo-700 px-8 py-4 rounded-full font-bold text-lg shadow-[0_10px_30px_rgba(0,0,0,0.2)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] flex items-center gap-2"
            >
              Book Consultation
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>

          </div>
        </div>
      </div>


      {/* About Section */}
      <section className="py-20 px-5 bg-gray-50">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-indigo-600 text-sm font-bold uppercase tracking-[0.2em] mb-3">Who We Are</h2>
            <h3 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">Centers of Excellence</h3>
            <p className="max-w-[700px] mx-auto text-lg text-gray-600 leading-relaxed">
              Kids Work Rehabilitation Center provides specialized therapy interventions in a playful and supportive environment. Our goal is to help children achieve independence and improve their quality of life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-200 to-purple-100 rounded-[2rem] rotate-2 opacity-50 group-hover:rotate-1 transition-transform duration-300"></div>
              <img
                src={about1}
                alt="Therapy Session"
                className="relative rounded-[1.5rem] shadow-xl w-full h-[400px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-3 rounded-full text-green-600">
                    <Smile className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">Play-Based Learning</h4>
                    <p className="text-sm text-gray-600">Making therapy fun and engaging</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-indigo-700">Why Choose Kids Work?</h3>
              <p className="text-gray-600 leading-relaxed">
                We understand that every child is unique. Our holistic approach integrates various therapeutic disciplines to create comprehensive treatment plans that address physical, cognitive, and emotional development.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {features.map((feature, index) => (
                  <div key={index} className="bg-white p-5 rounded-xl shadow-sm border border-indigo-50 hover:shadow-md transition-shadow">
                    <feature.icon className="w-8 h-8 text-indigo-600 mb-3" />
                    <h4 className="font-bold text-gray-800 mb-2">{feature.title}</h4>
                    <p className="text-sm text-gray-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-20 px-5 bg-white overflow-hidden">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1">
              <h2 className="text-indigo-600 text-sm font-bold uppercase tracking-[0.2em] mb-3">Our Expertise</h2>
              <h3 className="text-3xl md:text-5xl font-bold text-gray-800 mb-8 ">Specialized Programs</h3>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We specialize in early intervention and rehabilitation for a wide range of developmental conditions.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {programs.map((program, index) => (
                  <li key={index} className="flex items-center gap-3 p-3 bg-indigo-50/50 rounded-lg hover:bg-indigo-50 transition-colors">
                    <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
                    <span className="font-medium text-gray-700">{program}</span>
                  </li>
                ))}
              </ul>

            </div>

            <div className="flex-1 relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4 mt-8">
                  <img src={about2} alt="Therapy" className="rounded-2xl shadow-lg w-full h-[200px] object-cover hover:scale-105 transition-transform duration-500" />
                  <img src={about3} alt="Therapy" className="rounded-2xl shadow-lg w-full h-[260px] object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="space-y-4">
                  <img src={about4} alt="Therapy" className="rounded-2xl shadow-lg w-full h-[260px] object-cover hover:scale-105 transition-transform duration-500" />
                  <img src={about1} alt="Therapy" className="rounded-2xl shadow-lg w-full h-[200px] object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-5 bg-[#eef2ff]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-indigo-600 text-sm font-bold uppercase tracking-[0.2em] mb-3">Services</h2>
          <h3 className="text-3xl md:text-5xl font-bold text-gray-800 mb-12">Therapy Options</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Occupational Therapy', icon: Activity, color: 'bg-blue-500' },
              { title: 'Speech Therapy', icon: Users, color: 'bg-green-500' },
              { title: 'Special Education', icon: Brain, color: 'bg-orange-500' },
              { title: 'Physiotherapy', icon: Heart, color: 'bg-pink-500' },
            ].map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-[2rem] shadow-lg hover:-translate-y-2 hover:shadow-xl transition-all duration-300 group">
                <div className={`w-16 h-16 mx-auto ${service.color} rounded-2xl flex items-center justify-center text-white mb-6 shadow-md group-hover:rotate-6 transition-transform`}>
                  <service.icon className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-5  max-w-[1000px] mx-auto  ">
        <ImageCarousel />
      </section>
      {/* Add here the Crousel  */}


      {/* CTA Section */}
      <section className="py-20 px-5 bg-white relative overflow-hidden border-t border-indigo-50">
        <div className="max-w-[1000px] mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6  text-indigo-900">Ready to Start Your Journey?</h2>
          <p className="text-xl text-gray-600 mb-10 max-w-[700px] mx-auto leading-relaxed">
            Contact us today to schedule a consultation with our experienced team. We're here to support your child's growth every step of the way.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/branches" className="bg-indigo-600 text-white px-10 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl hover:bg-indigo-700 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2">
              Book Appointment <Calendar className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
